/**
 * The preference sheet: what the drafter must know before it plants anything for you, and the
 * list of words that get swapped out before anything you write leaves this device.
 *
 * It is the RECIPIENT's sheet the drafter is handed, never the author's — so this one governs
 * what lands on YOUR phone, which is why the hard nos on it are absolute.
 *
 * A full page rather than a tab. It is opened from Home and from the Signals box, and it is a
 * thing you fill in twice — a permanent tab for it would be advertising a form.
 *
 * ONE CARD, FIVE ROWS, AND A FOOTER THAT DOES NOT SCROLL AWAY.
 *
 * Each field used to be its own banded card, so a form of five short answers was five red panels
 * down a page with Save small and bottom-left underneath all of them. A form is a list: the label
 * is above its field and the hint under it, and the one control that commits the lot sits in a
 * footer that is always on screen.
 *
 * AND A FAILED LOAD IS NOT AN EMPTY SHEET. `PUT /api/preferences` writes every column from the
 * body it is given, so a GET that 500ed and rendered five blank fields was one Save away from
 * erasing somebody's hard nos — the one field on this page whose whole value is that it is never
 * ignored. Nothing here is editable, and Save is off with a reason, until a real sheet has landed.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api';
import { cn } from '@/lib/utils';
import { useStore } from '@/store';
import * as React from 'react';
import { ScrollView, View } from 'react-native';

interface Sheet {
  hardNos: string;
  softYeses: string;
  budgetCeiling: string;
  dislikes: string;
  names: string;
  updatedAt: string | null;
}

const EMPTY: Sheet = { hardNos: '', softYeses: '', budgetCeiling: '', dislikes: '', names: '', updatedAt: null };

/** The five answers, in the order the drafter weighs them. */
type FieldKey = 'hardNos' | 'softYeses' | 'budgetCeiling' | 'dislikes' | 'names';

const FIELDS: { key: FieldKey; label: string; hint: string; placeholder: string; max: number; line?: boolean }[] = [
  {
    key: 'hardNos',
    label: 'Hard nos',
    hint: 'Absolute. Nothing is ever planted that touches these.',
    placeholder: 'Surprise parties. Anything involving my mother.',
    max: 1000,
  },
  {
    key: 'softYeses',
    label: 'Soft yeses',
    hint: 'Where to lean when there is a choice.',
    placeholder: 'Outdoors, breakfast places, board games.',
    max: 1000,
  },
  {
    key: 'budgetCeiling',
    label: 'Budget ceiling',
    hint: 'For anything planted that costs money — a date card, an outing.',
    placeholder: '$60',
    max: 40,
    line: true,
  },
  {
    key: 'dislikes',
    label: 'Dislikes',
    hint: 'Not absolute, just avoid.',
    placeholder: 'Seafood. Loud bars.',
    max: 1000,
  },
  {
    key: 'names',
    label: 'Names and places',
    hint: 'Swapped out before anything you write leaves this device.',
    placeholder: 'Alex, Sam, "Bug" (pet name), Maple Street, Dot the dog',
    max: 1000,
  },
];

/** Whether anything on the sheet differs from the copy that came back from the server. */
function dirtyAgainst(sheet: Sheet, saved: Sheet): boolean {
  return FIELDS.some(({ key }) => sheet[key] !== saved[key]);
}

export function PreferenceSheet() {
  const { me, openOverlay, fail } = useStore();
  const look = useStore((s) => s.look);
  const [sheet, setSheet] = React.useState<Sheet>(EMPTY);
  /** The last thing the server said, and the only thing "dirty" is ever measured against. */
  const [saved, setSaved] = React.useState<Sheet>(EMPTY);
  const [loaded, setLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [justSaved, setJustSaved] = React.useState(false);
  const [confirmBack, setConfirmBack] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoadError(null);
    try {
      const current = await api.get<Sheet>('/api/preferences');
      setSheet(current);
      setSaved(current);
      setLoaded(true);
    } catch (e) {
      setLoadError(e instanceof Error && e.message.trim() ? e.message : 'The server did not answer.');
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const field = (key: FieldKey) => (value: string) => {
    setJustSaved(false);
    setSheet((s) => ({ ...s, [key]: value }));
  };

  const dirty = loaded && dirtyAgainst(sheet, saved);

  const save = async () => {
    setBusy(true);
    try {
      const written = await api.request<Sheet>('PUT', '/api/preferences', sheet);
      setSheet(written);
      setSaved(written);
      setJustSaved(true);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const leave = () => {
    setConfirmBack(false);
    openOverlay(null);
  };

  return (
    <View className="flex-1 gap-3">
      <View className="flex-row items-center gap-2">
        <Button variant="ghost" size="sm" onPress={() => (dirty ? setConfirmBack(true) : leave())}>
          <Text>← Back</Text>
        </Button>
        {/* The same cut every panel title takes — Bouquet italic, Tuxedo upright at `opsz` 144.
            See `panel.tsx`; this page is an overlay and draws its own header. */}
        <Text variant="title" className={look === 'bouquet' ? 'italic' : 'fit-opsz'}>
          Preference sheet
        </Text>
      </View>
      <Text variant="small" className="text-muted-foreground">
        Private · read by the drafter only
      </Text>

      <ScrollView className="flex-1" contentContainerClassName="gap-3 pb-6" keyboardShouldPersistTaps="handled">
        <Card>
          <CardContent>
            <CardDescription>
              {me?.partnerName ?? 'Your partner'} never sees this. The drafter reads it before it plants anything for
              you — a mission, a date card, a list item that will land on your phone — and it supplies the list of
              words swapped out before a signal ever leaves your device.
            </CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="gap-4">
            {loadError ? (
              <View className="flex-row flex-wrap items-center gap-2">
                <Text variant="small" className="text-destructive">
                  Couldn’t load your sheet
                </Text>
                <Text className="text-muted-foreground">·</Text>
                <Button variant="ghost" size="sm" flush={false} onPress={() => void load()}>
                  <Text>Try again</Text>
                </Button>
              </View>
            ) : null}

            {FIELDS.map(({ key, label, hint, placeholder, max, line }) => (
              <View key={key} className="gap-1.5">
                <Text className="text-body font-medium">{label}</Text>
                {line ? (
                  <Input
                    value={sheet[key]}
                    editable={loaded}
                    onChangeText={field(key)}
                    maxLength={max}
                    placeholder={placeholder}
                    autoComplete="off"
                  />
                ) : (
                  <Textarea
                    value={sheet[key]}
                    editable={loaded}
                    onChangeText={field(key)}
                    maxLength={max}
                    placeholder={placeholder}
                  />
                )}
                <Text variant="note">{hint}</Text>
              </View>
            ))}

            <Text variant="note">
              Names and places are a dictionary swap, not name detection: exactly what is listed there is replaced with
              placeholders before a signal reaches the drafter, and put back on the way home. Nothing else is guessed
              at.
            </Text>
          </CardContent>
        </Card>
      </ScrollView>

      {/**
       * THE FOOTER, which does not scroll. Save was under five cards' worth of scrolling, so the
       * one control that commits the page was only ever visible after you had finished reading it.
       */}
      {confirmBack ? (
        <Card className="border-destructive">
          <CardContent>
            <Text className="text-body font-medium">Discard changes?</Text>
            <CardDescription>
              Nothing on this sheet has been saved yet. Going back now leaves it exactly as the drafter last read it.
            </CardDescription>
            <View className="flex-row flex-wrap gap-2">
              <Button variant="destructive" onPress={leave}>
                <Text>Discard</Text>
              </Button>
              <Button variant="ghost" flush={false} onPress={() => setConfirmBack(false)}>
                <Text>Keep editing</Text>
              </Button>
            </View>
          </CardContent>
        </Card>
      ) : (
        <View className={cn('border-border flex-row flex-wrap items-center gap-3 border-t pt-3')}>
          <Button
            disabled={!loaded || busy}
            reason={loaded ? 'Saving…' : 'Your sheet has not loaded — saving now would overwrite it'}
            onPress={save}>
            <Text>Save</Text>
          </Button>
          {justSaved && !dirty ? (
            <Text variant="small" className="text-gold-text">
              Saved. Private.
            </Text>
          ) : dirty ? (
            <Text variant="small" className="text-muted-foreground">
              Unsaved changes
            </Text>
          ) : null}
        </View>
      )}
    </View>
  );
}
